import { useState, useCallback, useEffect } from 'react';
import { generateClient } from 'aws-amplify/api';
import { useLectureAPI } from './useLectureAPI';
import { validateLectureForEvent, lounges26Schema } from '../validation/lectures';

const client = generateClient();

/**
 * Hook for managing a list of lectures with enhanced functionality
 * This combines the CRUD operations with list management and validation
 */
export const useLecturesManager = (companyId, industryId, user = null) => {
  const [lectures, setLectures] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});

  // Use the lecture API hook for individual operations
  const { deleteLecture: deleteLectureAPI, isLoading: isDeletingLecture } = useLectureAPI();

  /**
   * Fetch all lectures for the company
   */
  const fetchLectures = useCallback(async () => {
    if (!companyId || !industryId) return [];

    setIsLoading(true);
    setError(null);
    
    let allLectures = [];
    let nextToken = null;
    
    try {
      do {
        const response = await client.graphql({
          variables: {
            filter: {
              companyLecturesId: {
                eq: companyId
              },
              createdAt: {
                gt: "2023-10-01T00:00:00.000Z"
              },
              type: {
                regexp: "(marketing|technical|educational|vip|marketing_wien|educational_wien|technical_wien|unset_lecture)"
              },
              industryLecturesId: {
                eq: industryId
              }
            },
            limit: 1000,
            nextToken: nextToken
          },
          query: /* GraphQL */ `
            query SearchLectures(
              $filter: SearchableLectureFilterInput
              $sort: [SearchableLectureSortInput]
              $limit: Int
              $nextToken: String
              $from: Int
              $aggregates: [SearchableLectureAggregationInput]
            ) {
              searchLectures(
                filter: $filter
                sort: $sort
                limit: $limit
                nextToken: $nextToken
                from: $from
                aggregates: $aggregates
              ) {
                items {
                  title {
                    language
                    text
                  }
                  event {
                    id
                    startDate
                    lectureEditEndDate
                    lectureDocumentsEndDate
                    lecturePresentationEndDate
                    name {
                      language
                      text
                    }
                  }
                  speakers {
                    items {
                      employee {
                        id
                        user {
                          firstName
                          lastName
                          middleName
                          title
                        }
                        image {
                          fileName
                          alt
                        }
                      }
                    }
                  }
                  subjects {
                    id
                    name {
                      language
                      text
                    }
                  } 
                  topics {
                    items {
                      topic {
                        id
                        name {
                          language
                          text
                        }
                      }
                    }
                  }         
                  content {
                    language
                    text
                  }
                  conditions {
                    name
                    accepted
                  }
                  image {
                    fileName
                    alt
                  }
                  pdfDe {
                    url
                    name
                    fileName
                    s3Path  
                  }
                  pdfEn {
                    url
                    name
                    fileName
                    s3Path  
                  }
                  presentation {
                    url
                    name
                    fileName
                    s3Path  
                  }
                  videoS3Key {
                    s3Path
                  }
                  company {
                    id
                    name
                    image {
                      main {
                        fileName
                        alt
                      }
                    }
                  }
                  videoProgress {
                    items {
                      id
                      userId
                      user {
                        id
                        title
                        firstName
                        lastName
                        image {
                          fileName
                        }
                        contact {
                          address {
                            company
                          }
                        }
                      }
                      videoId
                      progressInSeconds
                      totalWatchedInSeconds
                    }
                  }
                  id
                  session
                  adminComment
                  room
                  type
                  presentationLanguage
                  createdAt
                  updatedAt
                  companyLecturesId
                  eventLecturesId
                  startTime
                  endTime
                  lectureMainSpeakerId
                  __typename
                }
                nextToken
                total
                aggregateItems {
                  name
                  result {
                    ... on SearchableAggregateScalarResult {
                      value
                    }
                    ... on SearchableAggregateBucketResult {
                      buckets {
                        key
                        doc_count
                        __typename
                      }
                    }
                  }
                  __typename
                }
                __typename
              }
            }
          `
        });
        
        const lecturesBatch = response.data.searchLectures.items;
        allLectures = allLectures.concat(lecturesBatch);
        nextToken = response.data.searchLectures.nextToken;
      } while (nextToken);

      // Transform and sort lectures
      const transformedLectures = allLectures
        .map(lecture => ({
          ...lecture,
          topics: lecture.topics.items.map(topic => topic.topic)
        }))
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

      setLectures(transformedLectures);
      console.log("Fetched lectures:", transformedLectures);
      
      return transformedLectures;
    } catch (error) {
      console.log("Error fetching lectures:", error);
      setError(error.message || 'Failed to fetch lectures');
      
      // Try to set partial data if available
      if (error.data?.searchLectures?.items) {
        setLectures(error.data.searchLectures.items);
      }
      
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [companyId, industryId]);

  /**
   * Delete a lecture and update the local state
   */
  const deleteLecture = useCallback(async (lectureId) => {
    try {
      await deleteLectureAPI(lectureId);
      
      // Remove from local state
      setLectures(prevLectures => 
        prevLectures.filter(lecture => lecture.id !== lectureId)
      );
      
      console.log("Deleted lecture:", lectureId);
      return true;
    } catch (error) {
      console.log("Error deleting lecture:", error);
      setError(error.message || 'Failed to delete lecture');
      throw error;
    }
  }, [deleteLectureAPI]);

  /**
   * Add a new lecture to the local state
   */
  const addLecture = useCallback((newLecture) => {
    setLectures(prevLectures => {
      // Transform topics if needed
      const transformedLecture = {
        ...newLecture,
        // Ensure event relation exists
        event: newLecture.event || null,
        // Ensure speakers relation exists  
        speakers: newLecture.speakers || { items: [] },
        // Transform topics if needed
        topics: newLecture.topics?.items ? 
          newLecture.topics.items.map(topic => topic.topic) : 
          newLecture.topics || []
      };
      
      // Add to beginning of the list (newest first)
      return [transformedLecture, ...prevLectures];
    });
  }, []);

  /**
   * Update a lecture in the local state
   */
  const updateLecture = useCallback((updatedLecture) => {
    setLectures(prevLectures => {
      return prevLectures.map(lecture => {
        if (lecture.id === updatedLecture.id) {
          // Preserve existing relational data (event, speakers) if not provided in update
          const transformedLecture = {
            ...lecture, // Start with existing lecture data
            ...updatedLecture, // Override with updated data
            // Preserve event relation if not in update
            event: updatedLecture.event || lecture.event,
            // Handle speakers relation - preserve if not provided in update
            speakers: updatedLecture.speakers || lecture.speakers,
            // Transform topics if needed
            topics: updatedLecture.topics?.items ? 
              updatedLecture.topics.items.map(topic => topic.topic) : 
              updatedLecture.topics || lecture.topics || []
          };
          return transformedLecture;
        }
        return lecture;
      });
    });
  }, []);

  /**
   * Enhanced validation using the new validation system
   */
  const validateLectures = useCallback((lecturesToValidate = lectures) => {
    const errors = lecturesToValidate.map((lecture) => {
      // Transform lecture data to match validation schema expectations
      const transformedLecture = {
        ...lecture,
        // Transform speakers from speakers.items to direct array
        speakers: lecture.speakers?.items?.map(item => ({
          id: item.employee?.id || '',
          employeeId: item.employee?.id || '',
          firstName: item.employee?.user?.firstName || '',
          lastName: item.employee?.user?.lastName || '',
          middleName: item.employee?.user?.middleName,
          title: item.employee?.user?.title,
          department: item.employee?.department,
          image: item.employee?.image,
          type: item.type || (item.employee?.id === lecture.lectureMainSpeakerId ? 'main_speaker' : 'co_speaker'),
          name: `${item.employee?.user?.firstName || ''} ${item.employee?.user?.lastName || ''}`.trim(),
          company: item.employee?.company?.name || ''
        })) || [],
        // Transform topics from objects to string IDs
        topics: lecture.topics?.map(topic => 
          typeof topic === 'object' ? topic.id : topic
        ).filter(id => id) || [],
        // Transform subjects from GraphQL format to validation format
        subjects: lecture.subjects?.map(subject => ({
          de: subject.name?.find(n => n.language === 'de')?.text || '',
          en: subject.name?.find(n => n.language === 'en')?.text || ''
        })) || [],
        // Transform conditions to generalConditions if needed
        generalConditions: lecture.conditions?.filter(c => 
          ['submission_deadlines', 'advertising_free_content', 'recording_policy', 
           'speaker_expertise', 'program_scheduling', 'file_upload_deadlines'].includes(c.name)
        ).map(c => ({
          id: c.name,
          accepted: c.accepted
        })) || [],
        // Ensure handout field exists (combine pdfDe and pdfEn)
        handout: lecture.pdfDe || lecture.pdfEn ? {
          de: lecture.pdfDe ? {
            fileName: lecture.pdfDe.fileName || lecture.pdfDe.name,
            file: lecture.pdfDe
          } : undefined,
          en: lecture.pdfEn ? {
            fileName: lecture.pdfEn.fileName || lecture.pdfEn.name,
            file: lecture.pdfEn
          } : undefined
        } : undefined
      };
      
      // Use the new validation system for lectures
      const validationResult = validateLectureForEvent(transformedLecture, lounges26Schema);
      
      let lectureErrors = [];
      
      if (!validationResult.success) {
        lectureErrors = validationResult.errors.map(error => ({
          field: error.field,
          message: error.message
        }));
      }

      return {
        id: lecture.id,
        errors: lectureErrors
      };
    });
    
    // Convert array to object keyed by lecture ID for easier lookup
    const errorsObj = {};
    errors.forEach(error => {
      if (error.errors.length > 0) {
        errorsObj[error.id] = error.errors;
      }
    });
    
    setValidationErrors(errorsObj);
    return errorsObj;
  }, [lectures]);

  /**
   * Get validation errors for a specific lecture
   */
  const getLectureValidationErrors = useCallback((lectureId) => {
    return validationErrors[lectureId] || [];
  }, [validationErrors]);

  /**
   * Check if a lecture is valid (has no validation errors)
   */
  const isLectureValid = useCallback((lectureId) => {
    const errors = getLectureValidationErrors(lectureId);
    return errors.length === 0;
  }, [getLectureValidationErrors]);

  /**
   * Get lectures by status
   */
  const getLecturesByStatus = useCallback((status = 'all') => {
    switch (status) {
      case 'complete':
        return lectures.filter(lecture => isLectureValid(lecture.id));
      case 'incomplete':
        return lectures.filter(lecture => !isLectureValid(lecture.id));
      case 'scheduled':
        return lectures.filter(lecture => lecture.startTime);
      case 'unscheduled':
        return lectures.filter(lecture => !lecture.startTime);
      default:
        return lectures;
    }
  }, [lectures, isLectureValid]);

  /**
   * Can edit lecture check
   */
  const canEditLecture = useCallback((lecture) => {
    // Check if user has CompanyAdmin group
    const isCompanyAdmin = user?.["cognito:groups"]?.includes('CompanyAdmin');
    
    // If user is not CompanyAdmin, they cannot edit
    if (!isCompanyAdmin) {
      return false;
    }
    
    // Check time-based restrictions
    if ((lecture.event?.startDate && new Date(lecture.event.startDate) <= new Date()) || 
        (lecture.event?.lectureEditEndDate && new Date(lecture.event.lectureEditEndDate) < new Date())) {
      return false;
    }
    return true;
  }, [user]);

  /**
   * Can delete lecture check
   */
  const canDeleteLecture = useCallback((lecture) => {
    // Check if user has CompanyAdmin group
    const isCompanyAdmin = user?.["cognito:groups"]?.includes('CompanyAdmin');
    
    // If user is not CompanyAdmin, they cannot delete
    if (!isCompanyAdmin) {
      return false;
    }
    
    // Cannot delete if lecture has a start time
    if (lecture.startTime) {
      return false;
    }
    return true;
  }, [user]);

  // Auto-validate when lectures change
  useEffect(() => {
    if (lectures.length > 0) {
      const timer = setTimeout(() => {
        validateLectures(lectures);
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [lectures, validateLectures]);

  // Auto-fetch when companyId or industryId changes
  useEffect(() => {
    if (companyId && industryId) {
      fetchLectures();
    }
  }, [companyId, industryId, fetchLectures]);

  return {
    // Data
    lectures,
    validationErrors,
    
    // Loading states
    isLoading,
    isDeletingLecture,
    error,
    
    // Actions
    fetchLectures,
    deleteLecture,
    addLecture,
    updateLecture,
    
    // Validation
    validateLectures,
    getLectureValidationErrors,
    isLectureValid,
    
    // Filtering
    getLecturesByStatus,
    
    // Permission checks
    canEditLecture,
    canDeleteLecture,
    
    // Utility
    refreshLectures: fetchLectures
  };
};